import { useEffect, useRef } from 'react';

/**
 * Runs `focus` when `key` becomes a NEW non-null value, and at no other time.
 *
 * The effect lives here rather than in a component because this is the one shape the
 * no-direct-useEffect rule keeps for a reusable hook: syncing an external system (DOM focus)
 * to a value that changes outside React's control. The ref holds the previous key so the hook
 * can tell "newly set" from "still set" — it is not a guard bolted on to stop a misbehaving
 * effect from re-running, which is the pattern that rule warns about.
 *
 * Firing on every render instead would pull the caret out of whatever the person was typing in
 * the moment anything else re-rendered.
 */
export function useFocusOnNewKey(key: string | null | undefined, focus: () => void) {
  const previousKey = useRef<string | null | undefined>(undefined);
  const focusRef = useRef(focus);
  focusRef.current = focus;

  useEffect(() => {
    const previous = previousKey.current;
    previousKey.current = key;
    if (key && key !== previous) focusRef.current();
  }, [key]);
}
